import React from 'react';
import type ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { useTranslation } from 'react-i18next';
import { Download, FileImage, FileSpreadsheet, FileText } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ActionButton } from '@/shared/ui/action-button';
import { cn } from '@/lib/utils';
import { type ExportOptions, type ChartExportOptions } from '@/shared/lib/export';

import { useChartExport } from '../model/use-chart-export';
import { useFullscreenPortal } from '../model/use-fullscreen-portal';

interface ChartExportMenuProps {
  chartRef: React.RefObject<ReactECharts | null>;
  chartOption: EChartsOption | Record<string, unknown>;
  exportOptions?: {
    png?: ChartExportOptions;
    csv?: ExportOptions;
    xlsx?: ExportOptions;
  };
  disabled?: boolean;
  className?: string;
}

/**
 * Export menu component for ECharts
 * @example
 * const chartRef = useRef<ReactECharts>(null);
 * <ChartExportMenu
 *   chartRef={chartRef}
 *   chartOption={chartOption}
 *   exportOptions={{
 *     png: { filename: 'chart.png', pixelRatio: 2 },
 *     csv: { filename: 'data.csv' }
 *   }}
 * />
 */
export const ChartExportMenu: React.FC<ChartExportMenuProps> = ({
  chartRef,
  chartOption,
  exportOptions,
  disabled = false,
  className
}) => {
  const { t } = useTranslation();
  const { exportPNG, exportCSV, exportXLSX } = useChartExport({ chartOption, exportOptions, chartRef });
  const { container, onOpenChange } = useFullscreenPortal();

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <ActionButton intent="document" size="sm" disabled={disabled} className={className}>
          <Download />
          <span className="hidden md:inline">{t('welcome.export.label', 'Export')}</span>
        </ActionButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" container={container} className={cn(container && 'z-[1001]')}>
        <DropdownMenuItem onClick={exportPNG} data-testid="export-png">
          <FileImage className="h-4 w-4 mr-2" />
          {t('common.export.png', 'Export as PNG')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportCSV} data-testid="export-csv">
          <FileText className="h-4 w-4 mr-2" />
          {t('common.export.csv', 'Export as CSV')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportXLSX} data-testid="export-xlsx">
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          {t('common.export.xlsx', 'Export as Excel')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
